import { useDebouncedCallback } from "use-debounce";
import {
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  Loader,
  Query,
  Table,
  TableInstance,
} from "@monolith-forensics/monolith-ui";
import { CoinsIcon } from "lucide-react";

import LabAssetsAPI from "@/api/lab-assets";
import { ItemTotalBadge } from "@/Features/_components/Legacy";
import { formatCurrency } from "@/utils/currency-formatter";
import {
  resolveFilterState,
  toApiFilter,
  useTableState,
} from "@/Features/_shared";
import { LabAssetFlyout } from "../Flyout";
import { useLabAssetFilterDefs } from "../LabAssetFilterDefs";
import { labAssetQueryKeys } from "../labAssets.queryKeys";
import { dropAssetTypeRules, resolveOverdueFilter } from "../labAssets.utils";
import { useLabAssetColumnDefinitions } from "./useLabAssetColumnDefinitions";
import {
  GetLabAssetsQuery,
  LabAsset,
  LabAssetSortableColumn,
  LabAssetsTableQuery,
} from "../labAssets.types";

const DEFAULT_PAGE_SIZE = 100;

// Deliberately just { refresh }: spreading tableInstanceRef.current into the
// handle would freeze mount-time closures (mfui reassigns the instance every
// render). Widen with call-time delegation if a caller needs table methods.
export type LabAssetsTableHandle = {
  refresh: () => void;
};

/**
 * Assets table (ENG-925 shell + ENG-1667 column state + ENG-1668 flyout +
 * ENG-1666 search & filters): columns, infinite scroll, row navigation via the
 * asset-number cell, column sort/reorder/visibility persisted through
 * stateStorage, a per-row action button opening the properties flyout, and
 * table-menu search plus rule-based filters sent to POST /assets/query.
 */
export const LabAssetsTable: React.FC<{
  tableRef?: React.MutableRefObject<LabAssetsTableHandle | undefined>;
  query?: LabAssetsTableQuery;
  stateStoreKey?: string;
  onLoaded?: () => void;
  // When provided, row action clicks are delegated to the parent instead of
  // opening the built-in flyout (StorageTable's escape hatch, for future
  // case-scoped embeds).
  onActionButtonClick?: (rowData: LabAsset) => void;
  // Renders the table-menu "New Asset" button (EquipmentTable pattern) when
  // provided; the caller owns the create modal and the permission check.
  onCreateClick?: () => void;
}> = ({
  tableRef,
  query = {},
  stateStoreKey = "lab-assets:table",
  onLoaded,
  onActionButtonClick,
  onCreateClick,
}) => {
  const tableInstanceRef = useRef<TableInstance>();
  const [flyoutData, setFlyoutData] = useState<LabAsset | null>(null);
  const [showFlyout, setShowFlyout] = useState(false);

  const tableState = useTableState<GetLabAssetsQuery>(
    query as GetLabAssetsQuery,
    stateStoreKey,
    DEFAULT_PAGE_SIZE
  );

  // The table owns the raw rules (mfui renders the chips from them, incomplete
  // ones included) so a Hardware or Software view can drop an Asset Type rule.
  // tableState keeps only the resolved rules that reach the query.
  const [filterState, setFilterState] = useState<Query>(() => {
    try {
      const saved = JSON.parse(
        localStorage.getItem(stateStoreKey || "") || "{}"
      ).filterState;
      if (Array.isArray(saved?.rules)) return saved;
    } catch (error) {
      console.error("error", error);
    }
    return { combinator: "and", rules: [] };
  });

  const handleFilterChange = (filter: Query) => {
    setFilterState(filter);
    // resolveFilterState drops incomplete/mid-edit rules so they never reach
    // the query key and fire a fetch.
    tableState.setFilterState(resolveFilterState(filter));
  };

  // Adjusted during render rather than in an effect, so the toggle and the
  // rule never go out together in one request.
  if (query.asset_type) {
    const scoped = dropAssetTypeRules(filterState);
    if (scoped !== filterState) handleFilterChange(scoped);
  }

  // mfui persists only the rules a user edits, so persist the drop above too,
  // or a reload on All would bring the rule back.
  useEffect(() => {
    if (!stateStoreKey) return;
    try {
      const saved = JSON.parse(localStorage.getItem(stateStoreKey) || "{}");
      localStorage.setItem(
        stateStoreKey,
        JSON.stringify({ ...saved, filterState })
      );
    } catch (error) {
      console.error("error", error);
    }
  }, [stateStoreKey, filterState]);

  // resolvedQuery is a fresh object each render, but react-query hashes the
  // key structurally, so identity churn here is harmless.
  const resolvedQuery = tableState.resolvedQuery;
  const queryKey = labAssetQueryKeys.infiniteList(resolvedQuery);

  const {
    data,
    isLoading,
    isFetched,
    isFetchingNextPage,
    hasNextPage,
    refetch,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey,
    // POST /assets/query has a .strict() schema (400 on unknown keys), so map
    // the table-state shape to the API body explicitly — camelCase `pageSize`
    // must become `page_size`, and the mfui filter goes through toApiFilter
    // (the API validates operator labels against a closed enum).
    queryFn: ({ pageParam }) =>
      LabAssetsAPI.queryAssets({
        page: pageParam as number,
        page_size: resolvedQuery.pageSize ?? DEFAULT_PAGE_SIZE,
        // Trim here, not only in onSearch: mfui persists the raw input to
        // localStorage and useTableState restores it untouched, so a padded
        // or whitespace-only term would otherwise survive a reload and become
        // a `LIKE '% %'` search across every column.
        search: resolvedQuery.search?.trim() || undefined,
        // Newest first when the table state carries no sort: the API defaults
        // to asset_id ASC, which puts a just-created asset on the last (likely
        // unloaded) page — refresh() after a create would never show it in
        // labs with >100 assets. A user-chosen column sort overrides this.
        sort_by:
          (resolvedQuery.sort_by as LabAssetSortableColumn | undefined) ??
          "asset_id",
        sort_dir: resolvedQuery.sort_by ? resolvedQuery.sort_dir : "desc",
        // The "Validation overdue" preset is a virtual field; rewrite it to
        // the next_validation_due comparison first (resolveOverdueFilter).
        filter: toApiFilter(resolveOverdueFilter(resolvedQuery.filter)),
        asset_type: resolvedQuery.asset_type,
      }),
    // The assets API returns snake_case `next_page` (null when exhausted) —
    // unlike the storage API's `nextPage`.
    getNextPageParam: (lastPage) => lastPage.next_page,
    initialPageParam: 1,
    placeholderData: (previous) => previous,
  });

  const records = useMemo(
    () =>
      // connection.js resolves failed requests with { success: false } instead
      // of rejecting, so page.data can be undefined on an API error.
      data?.pages?.reduce<LabAsset[]>((acc, page) => {
        return [...acc, ...(page.data ?? [])];
      }, []) || [],
    [data?.pages]
  );

  // Guard against repeated scroll-to-bottom events: an unconditional
  // fetchNextPage() cancels and restarts the in-flight page request
  // (cancelRefetch defaults to true), so continued scrolling can starve the
  // next page indefinitely.
  const debouncedFetchNextPage = useDebouncedCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, 50);

  const handleActionButtonClick = (rowData: LabAsset) => {
    if (onActionButtonClick) {
      onActionButtonClick(rowData);
      return;
    }
    setFlyoutData(rowData);
    setShowFlyout(true);
  };

  // Flyout prev/next walk the loaded rows and wrap at the ends, matching
  // StorageTable (EquipmentTable doesn't wrap — it stops at the first row).
  const handleGetNextItem = (uuid: string) => {
    if (!uuid || records.length === 0) return;
    const current = records.findIndex((i) => i.uuid === uuid);
    if (current === -1) return;
    setFlyoutData(records[current + 1] || records[0]);
  };

  const handleGetPrevItem = (uuid: string) => {
    if (!uuid || records.length === 0) return;
    const current = records.findIndex((i) => i.uuid === uuid);
    if (current === -1) return;
    setFlyoutData(records[current - 1] || records[records.length - 1]);
  };

  useImperativeHandle(
    tableRef,
    () => ({
      refresh: () => {
        refetch();
      },
    }),
    [refetch]
  );

  const loading = isLoading || !isFetched || isFetchingNextPage;

  // Sum over the whole filtered set, not just the loaded pages (ENG-1146).
  // Undefined when the API predates the field or the request failed, so the
  // badge hides instead of showing a misleading $0.00.
  const totalCost = data?.pages?.[0]?.total_cost;

  useLayoutEffect(() => {
    if (isFetched) onLoaded?.();
  }, [isFetched, onLoaded]);

  const { columnDefinitions, isLoading: columnsLoading } =
    useLabAssetColumnDefinitions();
  const filterDefinitions = useLabAssetFilterDefs(query.asset_type);

  // Hold the Table until the custom-field columns exist: it applies the
  // persisted column state on mount, which would otherwise drop a shown or
  // reordered custom column. A failed definitions fetch falls back to the
  // static columns rather than blocking the table.
  if (columnsLoading) return <Loader />;

  return (
    <>
      <Table
        data={records}
        columnProps={columnDefinitions}
        keyField="uuid"
        virtualized={true}
        tableInstanceRef={tableInstanceRef}
        filterState={filterState}
        totalRecords={data?.pages?.[0]?.total || 0}
        loading={loading}
        focusedRowId={flyoutData?.uuid}
        stateStorage={{
          enabled: !!stateStoreKey,
          type: "localStorage",
          key: stateStoreKey || "",
        }}
        onScrollToBottom={debouncedFetchNextPage}
        onColumnHeaderClick={({ sort }) =>
          tableState.setSortState(sort || undefined)
        }
        enableSorting={true}
        enableColumnReorder={true}
        enableColumnResize={true}
        enableActionButton={true}
        onActionButtonClick={handleActionButtonClick}
        manualSearch={true}
        manualSorting={true}
        manualFiltering={true}
        manualExport={true}
        tableMenuOptions={{
          enabled: true,
          addButtonOptions: {
            enabled: !!onCreateClick,
            label: "New Asset",
            onClick: onCreateClick,
          },
          filterOptions: {
            enabled: true,
            showCombinator: false,
            // Asset Type is offered on the All view only (see filterState).
            filterDefinitions: query.asset_type
              ? filterDefinitions.filter(
                  (def) => def.dataField !== "asset_type"
                )
              : filterDefinitions,
            onFilterChange: handleFilterChange,
          },
          searchOptions: {
            enabled: true,
            placeholder: "Search Assets",
            // mfui's table menu already debounces the input 350ms before
            // calling onSearch, so no debounce wrapper here (StorageTable's
            // extra useDebouncedCallback predates that and double-delays).
            // Trim before the term enters table state so in-session query
            // keys agree with the body; the queryFn trims again for the
            // restored-from-storage path.
            onSearch: (term) => tableState.setSearchState(term.trim()),
          },
          tableCountOptions: {
            recordsTotalEnabled: true,
          },
          columnSelectorOptions: {
            enabled: true,
          },
          children: totalCost !== undefined && (
            <ItemTotalBadge
              total={formatCurrency(totalCost)}
              Icon={CoinsIcon}
              title="Total cost of all items shown"
            />
          ),
        }}
      />
      {showFlyout && flyoutData && (
        <LabAssetFlyout
          show={showFlyout}
          onClose={() => {
            setShowFlyout(false);
            setFlyoutData(null);
          }}
          defaultInfo={flyoutData}
          onNext={() => handleGetNextItem(flyoutData.uuid)}
          onPrevious={() => handleGetPrevItem(flyoutData.uuid)}
        />
      )}
    </>
  );
};
